import styled from 'styled-components';
import { Button, IconButton, LinkButton } from '@ardoq/button';
import { colors, radius, space } from '@ardoq/design-tokens';
import { ButtonWithDropdown } from '@ardoq/dropdown-menu';
import { Icon, IconSize } from '@ardoq/icons';
import { popoverTriggerAttributes } from '@ardoq/popovers';
import { IconBadge } from '@ardoq/status-ui';
import {
  ActionMenuButton,
  ActionMenuDivider,
  ActionMenuItem,
  ActionMenuLinkButton,
  CommonActionMenuItemProps,
  isAiButton,
  isDropdownButton,
  isIconButton,
  isLinkButton,
} from './types';
import { AiActionButton } from './AiActionButton';

const Divider = styled.hr<{ $size: ActionMenuDivider['size'] }>`
  background-color: ${colors.borderSubtle};
  height: ${({ $size }) => ($size === 'section' ? space.large : space.medium)};
  width: 1px;
  border-style: none;
  margin: 0;
  border-radius: ${radius.small};
`;

const BadgeAnchorWrapper = styled.span`
  position: relative;
  display: inline-flex;
`;

const BadgeAnchor = styled.span`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

type ActionButtonIconProps = {
  icon: NonNullable<ActionMenuButton['icon']>;
  badgeAnchorId?: string;
};

const ActionButtonIcon = ({ icon, badgeAnchorId }: ActionButtonIconProps) => {
  if (badgeAnchorId) {
    return (
      <BadgeAnchorWrapper>
        <ActionButtonIcon icon={icon} />
        <BadgeAnchor id={badgeAnchorId} aria-hidden="true" />
      </BadgeAnchorWrapper>
    );
  }
  if (!icon.statusDot) return <Icon iconName={icon.name} />;
  return (
    <IconBadge
      iconName={icon.name}
      iconSize={IconSize.MEDIUM}
      statusType={icon.statusDot.statusType}
      role="img"
      aria-label={icon.statusDot.label}
    />
  );
};

const toCommonActionButtonProps = (action: CommonActionMenuItemProps) => {
  return {
    buttonType: action.buttonType,
    isDisabled: action.state === 'disabled',
    isLoading: action.state === 'loading',
    isActive: action.state === 'active',
  };
};

const toPopoverTriggerProps = ({
  popoverTrigger,
  state,
  tooltipText,
}: ActionMenuButton) => {
  if (!popoverTrigger) return null;
  return {
    ...popoverTriggerAttributes(popoverTrigger),
    isActive: popoverTrigger.isOpen || state === 'active',
    'data-tooltip-text': popoverTrigger.isOpen ? '' : tooltipText,
  };
};

const ActionLinkButton = ({ action }: { action: ActionMenuLinkButton }) => {
  if (action.isIconOnly) {
    return (
      <LinkButton
        href={action.href}
        onClick={action.onClick}
        buttonType={action.buttonType}
        aria-label={action.label}
        data-tooltip-text={action.label}
      >
        <Icon
          iconName={action.icon.name}
          color={colors.icon}
          iconSize={IconSize.MEDIUM}
        />
      </LinkButton>
    );
  }

  return (
    <LinkButton
      href={action.href}
      onClick={action.onClick}
      buttonType={action.buttonType}
    >
      <Icon iconName={action.icon.name} />
      {action.label}
    </LinkButton>
  );
};

export const NewPageTopBarActionButton = ({
  action,
}: {
  action: ActionMenuItem;
}) => {
  if (action.type === 'divider') {
    return <Divider $size={action.size} />;
  }

  if (isDropdownButton(action)) {
    return (
      <ButtonWithDropdown
        options={action.options}
        maxDropdownSize={action.maxDropdownSize}
        data-tooltip-text={action.isIconOnly ? action.label : undefined}
        data-tour-id={action.dataTourId}
        hideChevron={action.isIconOnly ?? false}
        onMenuOpen={action.onMenuOpen}
        {...toCommonActionButtonProps(action)}
      >
        {action.icon && <Icon iconName={action.icon.name} />}
        {action.isIconOnly ? null : action.label}
      </ButtonWithDropdown>
    );
  }

  if (isAiButton(action)) {
    return <AiActionButton action={action} />;
  }

  if (isIconButton(action)) {
    return (
      <IconButton
        onClick={action.onClick}
        iconName={action.icon.name}
        label={action.label}
        {...toCommonActionButtonProps(action)}
      />
    );
  }

  if (isLinkButton(action)) {
    return <ActionLinkButton action={action} />;
  }

  return (
    <Button
      onClick={action.onClick}
      buttonSize={action.buttonSize}
      data-tooltip-text={action.tooltipText}
      data-tour-id={action.dataTourId}
      {...toCommonActionButtonProps(action)}
      {...toPopoverTriggerProps(action)}
    >
      {action.icon?.position !== 'right' && action.icon && (
        <ActionButtonIcon
          icon={action.icon}
          badgeAnchorId={action.badgeAnchorId}
        />
      )}
      {action.label}
      {action.icon?.position === 'right' && (
        <ActionButtonIcon
          icon={action.icon}
          badgeAnchorId={action.badgeAnchorId}
        />
      )}
    </Button>
  );
};
